import { MigrationService,TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import {
    AssessmentConfiguration,
    AwsMigrationAssessment,
    DatabaseSprintTeamComposition,
    DescriptionItem,
    DownloadAssessmentParams,
    EnumItem,
    MigrationResourceCosts,
    MigrationSprintTeamComposition,
    MigrationStrategySummary,
    MigrationToolsAndSchedule,
    SprintTeamComposition,
    TcoAssessment,
} from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Alert, Box, Button, Container, FormField, Header, Modal,
    NonCancelableCustomEvent, Select, SelectProps, SpaceBetween, Spinner, Tabs, TabsProps, Toggle, ToggleProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router';
import { useIsMounted } from 'react-tidy';

import { getId } from '../../common/arn';
import { HelpPanelTopicOptions } from '../../common/Constants';
import { constants } from '../../common/Localization';
import { MatchParam } from '../../common/types';
import { WEEKS_PER_YEAR } from '../../common/uomConversion';
import { deepCopy } from '../../common/util';
import { HelpPanelContext } from '../../contexts/HelpPanelContext';
import { PortfolioContext } from '../../contexts/PortfolioContext';
import { TcoContext } from '../../contexts/TcoContext';
import { updateTCOAssessment } from '../tco/TCOHelper';
import { CUSTOMER_LOCATION, DATABASE_SPRINT_TEAM, MIGRATION_RESOURCE_COSTS, MIGRATION_SPRINT_TEAM,
    MIGRATION_TOOLS_SCHEDULE, MIGRATION_TOOLS_SCHEDULE_CAPITALIZED, regionalConfigurationCategories, SprintResourceType
} from './constants';
import { MigrationData } from './interfaces';
import MigrationStrategyCount from './MigrationStrategyCount';
import MigrationSummaryBanner from './MigrationSummaryBanner';
import MigrationSummaryCost from './MigrationSummaryCost';
import MigrationSummaryCostResourceTable from './MigrationSummaryCostResourceTable';
import MigrationSummarySprintTeamResourceTable from './MigrationSummarySprintTeamResourceTable';
import MigrationSummaryToolsTraining from './MigrationSummaryToolsTraining';

const tcoService = new TcoService();
const migrationService = new MigrationService();

function MigrationSummary({ flash }: FlashContextInterface): JSX.Element {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const portfolioId = match?.params?.portfolioId ?? '';
    const tcoAssessmentId = match?.params?.tcoAssessmentId ?? null;
    
    const { assessment, assessmentToUpdate, assessmentDescription, updateTcoLoading, tcoLoading, updateAssessment, updateAssessmentToUpdate,
        assessmentMetadatas,
    } = useContext(TcoContext);
    const { portfolio } = useContext(PortfolioContext);

    const [ migrationAssessmentLoading, setMigrationAssessmentLoading ] = useState(false);
    const [ migrationAssessment, setMigrationAssessment ] = useState<AwsMigrationAssessment | null>(null);
    const [ customerLocation, setCustomerLocation ] = useState<string | null>();
    const [ confirmChangeLocationVisible, setConfirmChangeLocationVisible ] = useState(false);
    const [ overrideCosts, setOverrideCosts ] = useState(true);
    const [ overrideResources, setOverrideResources ] = useState(false);
    const [ migrationData, setMigrationData ] = useState<MigrationData | null>();
    const [ activeTabId, setActiveTabId ] = useState('migrationCost');
    const [ downloading, setDownloading ] = useState(false);
    const { panelHandler } = useContext(HelpPanelContext);

    const isMounted = useIsMounted();

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.MIGRATION_COST_SUMMARY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadMigrationAssessment();
        if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
            setCustomerLocation(assessment.configuration.migrationToolsAndSchedule.customerLocation);
            setMigrationData({
                configuration: {
                    databaseSprintTeamComposition: deepCopy(assessment.configuration.databaseSprintTeamComposition),
                    migrationSprintTeamComposition: deepCopy(assessment.configuration.migrationSprintTeamComposition),
                },
                costConfiguration: {
                    migrationResourceCosts: deepCopy(assessment.configuration.migrationResourceCosts),
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const loadMigrationAssessment = async () => {
        if (isMounted()) {
            setMigrationAssessmentLoading(true);
        }
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const migrationAssessment = await tcoService.getAwsMigration(tcoAssessmentId);
                if (isMounted()) {
                    setMigrationAssessment(migrationAssessment);
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, '');
            }
        } finally {
            if (isMounted()) {
                setMigrationAssessmentLoading(false);
            }
        }
    };

    const getEnumOptionsByIdentifier = (descriptions: DescriptionItem[], sectionIdentifier: string, fieldIdentifier: string): EnumItem[] => {
        const section = descriptions.find(description => description.identifier === sectionIdentifier);
        if (section) {
            const field = section.properties.find(property => property.identifier === fieldIdentifier);
            return field?.enum ?? [];
        }
        return [];
    };

    const getLocationOptions = (): SelectProps.Options => {
        const enumItems = getEnumOptionsByIdentifier(assessmentDescription, MIGRATION_TOOLS_SCHEDULE , CUSTOMER_LOCATION);
        return enumItems.map(enumItem => {
            return {
                value: enumItem.value,
                label: enumItem.text,
            };
        });
    };

    const getSelectedLocationOption = (): SelectProps.Option | null => {
        const options = getLocationOptions();
        const selectedItem = options.find((option: SelectProps.Option) => option.value === customerLocation);
        return selectedItem ?? null;
    };

    const selectedLocationChanged = (event: NonCancelableCustomEvent<SelectProps.ChangeDetail>) => {
        setCustomerLocation(event.detail.selectedOption.value ?? null);
        setConfirmChangeLocationVisible(true);
    };

    const overrideCostsChanged = (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
        setOverrideCosts(event.detail.checked);
    };

    const overrideResourcesChanged = (event: NonCancelableCustomEvent<ToggleProps.ChangeDetail>) => {
        setOverrideResources(event.detail.checked);
    };

    const onConfirmChangeLocation = () => {
        setConfirmChangeLocationVisible(false);
        if (assessmentToUpdate) {
            if (!customerLocation || !migrationData) {
                setCustomerLocation(assessmentToUpdate.configuration.migrationToolsAndSchedule.customerLocation ?? null);
                return;
            }
            
            assessmentToUpdate.configuration.migrationToolsAndSchedule.customerLocation = customerLocation;
            
            // Find the property for each of the four categories with the region code prefix that matches the selected region
            // Use those values to set the assessment configuration values if the appropriate override toggle is on
            regionalConfigurationCategories.forEach(category => {
                const newLocationCategory = `${customerLocation}${category}`;
                if (assessmentToUpdate.configuration.hasOwnProperty(newLocationCategory)) {
                    const newLocationCategoryConfiguration = assessmentToUpdate.configuration[newLocationCategory as keyof AssessmentConfiguration];
                    switch (category) {
                        case MIGRATION_TOOLS_SCHEDULE_CAPITALIZED:
                            assessmentToUpdate.configuration.migrationToolsAndSchedule.trainingCost = 
                                (newLocationCategoryConfiguration as MigrationToolsAndSchedule).trainingCost;
                            break;
                        case MIGRATION_RESOURCE_COSTS:
                            assessmentToUpdate.configuration.migrationResourceCosts = overrideCosts ? 
                                newLocationCategoryConfiguration as MigrationResourceCosts : migrationData.costConfiguration.migrationResourceCosts;
                            break;
                        case DATABASE_SPRINT_TEAM:
                            assessmentToUpdate.configuration.databaseSprintTeamComposition = overrideResources ? 
                                newLocationCategoryConfiguration as DatabaseSprintTeamComposition : migrationData.configuration.databaseSprintTeamComposition;
                            break;
                        case MIGRATION_SPRINT_TEAM:
                            assessmentToUpdate.configuration.migrationSprintTeamComposition = overrideResources ?
                                newLocationCategoryConfiguration as MigrationSprintTeamComposition : migrationData.configuration.migrationSprintTeamComposition;
                    }
                }
            });
            setMigrationData({
                configuration: {
                    migrationSprintTeamComposition: deepCopy(assessmentToUpdate.configuration.migrationSprintTeamComposition),
                    databaseSprintTeamComposition: deepCopy(assessmentToUpdate.configuration.databaseSprintTeamComposition),
                },
                costConfiguration: {
                    migrationResourceCosts: deepCopy(assessmentToUpdate.configuration.migrationResourceCosts),
                }
            });

            updateTcoAssessment();
        }
    };

    const updateTcoAssessment = async () => {
        try {
            updateTcoLoading(true);
            if (assessmentToUpdate) {
                const updatedAssessment = {...assessmentToUpdate, configuration: assessmentToUpdate.configuration};
                const tcoAssessment = await updateTCOAssessment(portfolioId, updatedAssessment, tcoService);
                updateAssessment(tcoAssessment);
                await loadMigrationAssessment();
            }
        } catch (err) {
            flash.alert(FlashType.error, (err as Error).message, '');
        } finally {
            updateTcoLoading(false);
        }
    };

    const cancelChangeLocation = () => {
        setCustomerLocation(assessmentToUpdate?.configuration.migrationToolsAndSchedule?.customerLocation ?? null);
        setConfirmChangeLocationVisible(false);
    };

    const regionSelection = () => {
        if (tcoLoading || migrationAssessmentLoading) {
            return null;
        }
        const selectRegion = (
            <FormField
                label={constants.MIGRATION_LOCATION}
            >
                <Select 
                    options={getLocationOptions()}
                    selectedOption={getSelectedLocationOption()}
                    onChange={selectedLocationChanged}
                    disabled={assessmentToUpdate?.configuration.amerMigrationResourceCosts == null}
                />
            </FormField>
        );

        if (assessmentToUpdate?.configuration.migrationToolsAndSchedule.customerLocation == null) {
            return (
                <SpaceBetween size='s'>
                    <Alert type='warning'>{constants.LOCATION_NOT_SET_WARNING}</Alert>
                    {selectRegion}
                </SpaceBetween>
            );
        } else {
            return selectRegion;
        }
    };

    const updateSprintData = (resourceType: SprintResourceType) => (sprintTeam: SprintTeamComposition, resourceCosts: MigrationResourceCosts) => {
        if (!assessmentToUpdate) {
            return;
        }
        assessmentToUpdate.configuration.migrationResourceCosts = resourceCosts;
        if (resourceType === SprintResourceType.SERVER) {
            assessmentToUpdate.configuration.migrationSprintTeamComposition = sprintTeam;
        } else {
            assessmentToUpdate.configuration.databaseSprintTeamComposition = sprintTeam;
        }
        updateTcoAssessment();
    };

    const activeTabChanged = (event: NonCancelableCustomEvent<TabsProps.ChangeDetail>) => {
        setActiveTabId(event.detail.activeTabId);
    };

    const tabs = tcoLoading || migrationAssessmentLoading ? [] : ([
        {
            label: constants.MIGRATION_COST,
            id: 'migrationCost',
            content: migrationAssessment?.output.migration ? (
                <MigrationSummaryCost
                    migration={migrationAssessment.output.migration}
                />
            ) : null
        },
        {
            label: constants.SERVER_RESOURCES,
            id: 'serverResources',
            content: migrationAssessment?.output.migration ? (
                <MigrationSummaryCostResourceTable
                    currency={migrationAssessment.output.migration.currency}
                    sprints={migrationAssessment.output.migration.sprints} 
                />
            ) : null
        },
        {
            label: constants.DATABASE_RESOURCES,
            id: 'databaseResources',
            content: migrationAssessment?.output.migration?.database ? (
                <MigrationSummaryCostResourceTable
                    currency={migrationAssessment.output.migration.currency}
                    sprints={migrationAssessment.output.migration.database.sprints}
                />
            ) : (
                <Box>{constants.RECALCULATE_DATABASE_WARNING}</Box>
            )
        },
        {
            label: constants.TOOLS_AND_TRAINING,
            id: 'toolsAndTraining',
            content: migrationAssessment?.output.migration ? (
                <MigrationSummaryToolsTraining
                    migration={migrationAssessment.output.migration}
                />
            ) : null
        },
        {
            label: constants.SERVER_SPRINT_TEAM_RESOURCES,
            id: 'serverSprint',
            content: migrationAssessment?.output.migration && migrationData ? (
                <MigrationSummarySprintTeamResourceTable
                    currency={migrationAssessment.output.migration.currency}
                    migrationCostSummary={migrationAssessment.output.migration.summary}
                    migrationResourceCost={migrationData.costConfiguration.migrationResourceCosts}
                    sprintTeam={migrationData.configuration.migrationSprintTeamComposition}
                    updateTco={updateSprintData(SprintResourceType.SERVER)}
                />
            ) : null
        },
        {
            label: constants.DATABASE_SPRINT_TEAM_RESOURCES,
            id: 'databaseSprint',
            content: migrationAssessment?.output.migration.database && migrationData ? (
                <MigrationSummarySprintTeamResourceTable
                    currency={migrationAssessment.output.migration.currency}
                    migrationCostSummary={migrationAssessment.output.migration.database.summary}
                    migrationResourceCost={migrationData.costConfiguration.migrationResourceCosts}
                    sprintTeam={migrationData.configuration.databaseSprintTeamComposition}
                    updateTco={updateSprintData(SprintResourceType.DATABASE)}
                />
            ) : null
        }
    ]);

    const changeLocationModal = confirmChangeLocationVisible ? (
        <Modal
            visible={confirmChangeLocationVisible}
            header={constants.RESET_MIGRATION_VALUES}
            onDismiss={cancelChangeLocation}
            footer={(
                <Box float='right'>
                    <SpaceBetween size='s' direction='horizontal'>
                        <Button variant='link' onClick={cancelChangeLocation}>{constants.CANCEL_BUTTON_TEXT}</Button>
                        <Button onClick={onConfirmChangeLocation}>{constants.CONTINUE_BUTTON_TEXT}</Button>
                    </SpaceBetween>
                </Box>
            )}
        >
            <Box>
                <SpaceBetween size='xxs'>
                    {constants.CHANGE_LOCATION_WARNING_P1}
                    <Toggle
                        checked={overrideCosts}
                        onChange={overrideCostsChanged}
                    >
                        {constants.OVERRIDE_RATES}
                    </Toggle>
                    <Toggle
                        checked={overrideResources}
                        onChange={overrideResourcesChanged}
                    >
                        {constants.OVERRIDE_RESOURCES}
                    </Toggle>
                    {constants.CHANGE_LOCATION_WARNING_P2}
                </SpaceBetween>
            </Box>
        </Modal>
    ) : null;

    const updateServerStrategyCountOverride = (isOverride: boolean) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.serverStrategyCountOverride = isOverride;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const updateDatabaseStrategyCountOverride = (isOverride: boolean) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.databaseStrategyCountOverride = isOverride;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const updateServerInputSummary = (updatedInputSummary: MigrationStrategySummary[]) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.migrationStrategySummary = updatedInputSummary;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const updateDatabaseInputSummary = (updatedInputSummary: MigrationStrategySummary[]) => {
        const updatedAssessment: TcoAssessment = deepCopy(assessmentToUpdate);
        updatedAssessment.input.migration.databaseMigrationStrategySummary = updatedInputSummary;
        updateAssessmentToUpdate(updatedAssessment);
    };

    const downloadMigrationSummary = async (): Promise<void> => {
        setDownloading(true);
        try {
            if (migrationAssessment?._references) {
                const assessmentMetadata = assessmentMetadatas.find(metadata => metadata.id === tcoAssessmentId);
                const migrationAssessmentId = getId(migrationAssessment._references, 'migration-assessment') ?? '';
                const params: DownloadAssessmentParams = {
                    timezoneOffset: new Date().getTimezoneOffset(),
                    accountName: portfolio.accountName ?? '',
                    portfolioName: portfolio.name,
                    assessmentName: assessmentMetadata?.name || '',
                };

                const url = await migrationService.download(migrationAssessmentId, params);
                const win = window.open(url);
                if (win === undefined) {
                    flash.alert(FlashType.warning, constants.ERROR_OPENING_FILE_URL_CONTENT, constants.ERROR_OPENING_FILE_URL_HEADER);
                }
            }
        } catch (err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_DOWNLOADING_MAINFRAME_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                setDownloading(false);
            }
        }
    };
    
    const getContent = () => {
        if (migrationAssessmentLoading || tcoLoading) {
            return (
                <Spinner size='large'/>
            );
        }
        else if (migrationAssessment?.output.migration != null && assessment && assessmentToUpdate) {
            return (
                <SpaceBetween size='m'>
                    <MigrationSummaryBanner 
                        migration={migrationAssessment.output.migration}
                        durationYears={Number(assessment.configuration.migrationToolsAndSchedule.projectDurationWeeks) / WEEKS_PER_YEAR}
                        downloading={downloading}
                        downloadFn={downloadMigrationSummary}
                    />
                    <Container>
                        <SpaceBetween size='m'>
                            <MigrationStrategyCount
                                serverMigrationStrategySummary={migrationAssessment.output.migration.migrationStrategySummaryForPortfolio ?? []}
                                serverInputSummary={assessmentToUpdate.input.migration.migrationStrategySummary ?? []}
                                databaseMigrationStrategySummary={migrationAssessment.output.migration.databaseMigrationStrategySummaryForPortfolio ?? []}
                                databaseInputSummary={assessmentToUpdate.input.migration.databaseMigrationStrategySummary ?? []}
                                serverStrategyCountOverride={assessmentToUpdate.input.migration.serverStrategyCountOverride ?? false}
                                databaseStrategyCountOverride={assessmentToUpdate.input.migration.databaseStrategyCountOverride ?? false}
                                updateDatabaseStrategyCountOverride={updateDatabaseStrategyCountOverride}
                                updateServerStrategyCountOverride={updateServerStrategyCountOverride}
                                updateDatabaseInputSummary={updateDatabaseInputSummary}
                                updateServerInputSummary={updateServerInputSummary}
                            />
                            <SpaceBetween size='xxs'>
                                <Header variant='h2'>{constants.COST_DETAILS}</Header>
                                {regionSelection()}
                                <Tabs 
                                    tabs={tabs}
                                    activeTabId={activeTabId}
                                    onChange={activeTabChanged}
                                />
                            </SpaceBetween>
                            {changeLocationModal}
                        </SpaceBetween>
                    </Container>
                </SpaceBetween>
            );
        }
        else {
            return (
                <Alert type='warning'>{constants.RECALCULATE_PORTFOLIO_WARNING}</Alert>
            );
        }
    };

    return (
        getContent()
    );
}

export default withFlashContext(MigrationSummary);
